import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../config';

function Channels() {
  const [isLoading, setIsLoading] = useState(true);
  const [activeChannels, setActiveChannels] = useState([]);
  const [userChannels, setUserChannels] = useState({});
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showTelegramTestModal, setShowTelegramTestModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [telegramTestResult, setTelegramTestResult] = useState(null);

  // WhatsApp config fields
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [apiVersion, setApiVersion] = useState('v18.0');
  const [adminSenderId, setAdminSenderId] = useState(''); // NEW

  // Telegram config fields
  const [botToken, setBotToken] = useState('');
  const [botUsername, setBotUsername] = useState('');
  const [adminSenderIdTelegram, setAdminSenderIdTelegram] = useState(''); // NEW

  useEffect(() => {
    const fetchChannelData = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        console.log('Fetching channel data from API...');
        const response = await axios.get(`${API_URL}/channels/user`);
        
        console.log('Channels API response:', response.data);
        
        // Check if response has the expected structure
        if (response.data.activeChannels !== undefined) {
          setActiveChannels(response.data.activeChannels || []);
          setUserChannels(response.data.userChannels || {});
          
          // Reset form fields based on existing configurations
          if (response.data.userChannels?.WhatsApp) {
            const whatsappConfig = response.data.userChannels.WhatsApp.config_data;
            setPhoneNumberId(whatsappConfig.phone_number_id || '');
            setAccessToken(whatsappConfig.access_token || '');
            setBusinessId(whatsappConfig.business_id || '');
            setApiVersion(whatsappConfig.api_version || 'v18.0');
            setAdminSenderId(response.data.userChannels.WhatsApp.admin_sender_id || ''); // NEW
          }
          
          if (response.data.userChannels?.Telegram) {
            const telegramConfig = response.data.userChannels.Telegram.config_data;
            setBotToken(telegramConfig.bot_token || '');
            setBotUsername(telegramConfig.bot_username || '');
            setAdminSenderIdTelegram(response.data.userChannels.Telegram.admin_sender_id || ''); // NEW
          }
        } else {
          throw new Error('Invalid response format from server');
        }
        
      } catch (err) {
        console.error('Error fetching channel data:', err);
        
        if (err.response?.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to load channel data. Please check your connection.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchChannelData();
  }, []);

  const handleWhatsAppSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      const response = await axios.post(`${API_URL}/channels/whatsapp`, {
        phoneNumberId,
        accessToken,
        businessId,
        apiVersion,
        adminSenderId // NEW
      });
      
      setSuccess('WhatsApp configuration saved successfully');
      setShowWhatsAppModal(false);
      
      // Refresh data
      const updatedData = await axios.get(`${API_URL}/channels/user`);
      setUserChannels(updatedData.data.userChannels || {});
    } catch (err) {
      console.error('Error saving WhatsApp config:', err);
      setError(err.response?.data?.message || 'Error saving WhatsApp configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTelegramSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      const response = await axios.post(`${API_URL}/channels/telegram`, {
        botToken,
        botUsername,
        adminSenderId: adminSenderIdTelegram // NEW
      });
      
      setSuccess('Telegram configuration saved successfully');
      setShowTelegramModal(false);
      
      // Refresh data
      const updatedData = await axios.get(`${API_URL}/channels/user`);
      setUserChannels(updatedData.data.userChannels || {});
    } catch (err) {
      console.error('Error saving Telegram config:', err);
      setError(err.response?.data?.message || 'Error saving Telegram configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTelegramTest = async () => {
    try {
      setTelegramTestResult(null);
      setIsTesting(true);
      
      const response = await axios.post(`${API_URL}/channels/telegram/test`, {
        botToken
      });
      
      setTelegramTestResult({
        success: true,
        message: response.data.message,
        botInfo: response.data.botInfo,
        webhookInfo: response.data.webhookInfo
      });
    } catch (err) {
      console.error('Error testing Telegram connection:', err);
      setTelegramTestResult({
        success: false,
        message: err.response?.data?.message || 'Connection test failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading channel data...</p>
      </div>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="m-0">Channel Configuration</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <p>Configure the messaging channels you want to use with your AI assistant.</p>
          
          <Row className="mt-4">
            {activeChannels.map(channel => (
              <Col md={6} className="mb-4" key={channel.id}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="m-0">
                      {channel.name === 'WhatsApp' && (
                        <FontAwesomeIcon icon={faWhatsapp} className="text-success me-2" />
                      )}
                      {channel.name === 'Telegram' && (
                        <FontAwesomeIcon icon={faTelegram} className="text-primary me-2" />
                      )}
                      {channel.name}
                    </h5>
                    
                    {userChannels[channel.name] ? (
                      <span className="badge bg-success">Configured</span>
                    ) : (
                      <span className="badge bg-secondary">Not Configured</span>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {channel.name === 'WhatsApp' && (
                      <>
                        <p>Connect your WhatsApp Business account to enable AI responses through WhatsApp.</p>
                        
                        <Button 
                          variant="primary" 
                          className="mt-2" 
                          onClick={() => setShowWhatsAppModal(true)}
                        >
                          {userChannels.WhatsApp ? 'Edit Configuration' : 'Configure WhatsApp'}
                        </Button>
                        
                        {userChannels.WhatsApp && (
                          <div className="mt-3 alert alert-info">
                            <h6>Webhook URL (configure in Meta dashboard):</h6>
                            <code>{userChannels.WhatsApp.webhook_url}</code>
                            
                            <h6 className="mt-3">Verification Token:</h6>
                            <code>{userChannels.WhatsApp.webhook_token}</code>
                          </div>
                        )}
                      </>
                    )}
                    
                    {channel.name === 'Telegram' && (
                      <>
                        <p>Connect your Telegram Bot to enable AI responses through Telegram messages.</p>
                        
                        <Button 
                          variant="primary" 
                          className="mt-2" 
                          onClick={() => setShowTelegramModal(true)}
                        >
                          {userChannels.Telegram ? 'Edit Configuration' : 'Configure Telegram'}
                        </Button>
                        
                        {userChannels.Telegram && (
                          <div className="mt-3 alert alert-info">
                            <p>Webhook has been automatically configured with Telegram.</p>
                            <p>To test, send a message to your bot: <strong>@{userChannels.Telegram.config_data.bot_username}</strong></p>
                          </div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* WhatsApp Configuration Modal */}
      <Modal show={showWhatsAppModal} onHide={() => setShowWhatsAppModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faWhatsapp} className="text-success me-2" />
            WhatsApp Configuration
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleWhatsAppSubmit}>
          <Modal.Body>
            <Alert variant="info">
              <h6><i className="fas fa-info-circle me-2"></i>How to get WhatsApp Cloud API credentials:</h6>
              <ol>
                <li>Go to the <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">Meta Developers</a> portal</li>
                <li>Create or select your app</li>
                <li>Add WhatsApp product to your app</li>
                <li>Go to API Setup page to get your Phone Number ID</li>
                <li>Generate a Permanent Access Token from the app's Settings &gt; Advanced &gt; Personal Access Token section</li>
                <li>After saving, use the provided webhook URL in your app configuration</li>
              </ol>
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Phone Number ID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.target.value)}
                placeholder="Example: 123456789012345"
                required
              />
              <Form.Text className="text-muted">
                The ID of your WhatsApp phone number (found in API Setup page)
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Permanent Access Token <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showAccessToken ? "text" : "password"}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="Example: EAAxxxx..."
                  required
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => setShowAccessToken(!showAccessToken)}
                >
                  <FontAwesomeIcon icon={showAccessToken ? faEyeSlash : faEye} />
                </Button>
              </div>
              <Form.Text className="text-muted">
                Your permanent access token for WhatsApp API calls (starts with EAA...)
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Business Account ID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                placeholder="Example: 987654321098765"
                required
              />
              <Form.Text className="text-muted">
                Your WhatsApp Business Account ID
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">API Version</Form.Label>
              <Form.Select value={apiVersion} onChange={(e) => setApiVersion(e.target.value)}>
                <option value="v18.0">v18.0</option>
                <option value="v17.0">v17.0</option>
                <option value="v16.0">v16.0</option>
                <option value="v15.0">v15.0</option>
              </Form.Select>
              <Form.Text className="text-muted">
                WhatsApp API version (leave as default unless you need a specific version)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Admin Number / Sender ID</Form.Label>
              <Form.Control
                type="text"
                value={adminSenderId}
                onChange={e => setAdminSenderId(e.target.value)}
                placeholder="E.g. +1234567890 or chat_id"
              />
              <Form.Text className="text-muted">
                This number will be used for admin actions (e.g. MCP commands).
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWhatsAppModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Telegram Configuration Modal */}
      <Modal show={showTelegramModal} onHide={() => setShowTelegramModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTelegram} className="text-primary me-2" />
            Telegram Configuration
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTelegramSubmit}>
          <Modal.Body>
            <Alert variant="info">
              <h6>How to create and get Telegram bot credentials:</h6>
              <ol>
                <li>Open Telegram and search for <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a></li>
                <li>Send the command: <code>/newbot</code></li>
                <li>Follow the instructions to name your bot</li>
                <li>BotFather will provide a token - copy and paste it below</li>
                <li>The system will automatically configure the webhook URL</li>
              </ol>
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label>Bot Token</Form.Label>
              <Form.Control
                type="text"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Bot Username (without @)</Form.Label>
              <Form.Control
                type="text"
                value={botUsername}
                onChange={(e) => setBotUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Admin Number / Sender ID</Form.Label>
              <Form.Control
                type="text"
                value={adminSenderIdTelegram}
                onChange={e => setAdminSenderIdTelegram(e.target.value)}
                placeholder="E.g. +1234567890 or chat_id"
              />
              <Form.Text className="text-muted">
                This number will be used for admin actions (e.g. MCP commands).
              </Form.Text>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="outline-success" 
                type="button"
                onClick={() => setShowTelegramTestModal(true)}
                disabled={!botToken}
              >
                Test Connection
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTelegramModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Telegram Test Connection Modal */}
      <Modal show={showTelegramTestModal} onHide={() => setShowTelegramTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTelegram} className="text-primary me-2" />
            Test Telegram Connection
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will test your Telegram bot token and webhook configuration.</p>
          
          <div className="mb-3">
            <Form.Label>Bot Token</Form.Label>
            <Form.Control 
              type="text" 
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              required
            />
          </div>
          
          {isTesting && (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Testing connection...</span>
              </div>
              <p className="mt-2">Testing Telegram bot connection...</p>
            </div>
          )}
          
          {telegramTestResult && (
            <Alert variant={telegramTestResult.success ? 'success' : 'danger'}>
              <Alert.Heading>{telegramTestResult.success ? 'Success!' : 'Error'}</Alert.Heading>
              <p>{telegramTestResult.message}</p>
              {telegramTestResult.success && telegramTestResult.botInfo && (
                <div className="mt-2">
                  <strong>Bot Information:</strong>
                  <ul className="mt-1 mb-0">
                    <li><strong>Name:</strong> {telegramTestResult.botInfo.first_name}</li>
                    <li><strong>Username:</strong> @{telegramTestResult.botInfo.username}</li>
                    <li><strong>ID:</strong> {telegramTestResult.botInfo.id}</li>
                  </ul>
                  {telegramTestResult.webhookInfo && (
                    <div className="mt-2">
                      <strong>Webhook Status:</strong>
                      <p className="mb-0">
                        {telegramTestResult.webhookInfo.url ? 
                          `Active: ${telegramTestResult.webhookInfo.url}` : 
                          'No webhook configured'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTelegramTestModal(false)}>
            Close
          </Button>
          <Button 
            variant="success" 
            onClick={handleTelegramTest}
            disabled={isTesting || !botToken}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Channels;
